/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @coauthor David Roman <david@inarix.com>
 * @file LicencesTable.tsx
 * @desc Created on Mon Apr 28 2025 20:44:52
 * @copyright All rights reserved @ Inarix
 */

import { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, Modal, Snackbar, ThemeProvider } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { TFunction } from "i18next";

import themeInarix from "../StylesMuiInarix";
import { selectLocale } from "../../redux/slices/layout";
import {
  focusedLicense,
  licenseAssignation,
  clearLicenseNotification,
  selectLicenseLoading,
  selectLicenseNotification,
  selectModalLicense,
  setLicenseId,
  LicensesFilters,
  setShowFilterForm,
  setPreselectedFilterField,
  selectUnassignedCount,
  selectExpiredCount,
  selectTotalLicensesCount,
  setFilterModel,
  setOpenCells,
  selectUpcomingCount,
} from "../../redux/slices/licenses/licenseList";
import { LicensePacksModal } from "./LicensePacksModal";
import { useLicenseColumns } from "./LicenceColumns";
import LicenseAssignationModal from "./LicenseAssignationModal";
import {
  getDataGridLocale,
  managingFiltersText,
} from "../data/dataHelpers/DataProgress";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";
import { GroupCell } from "./GroupCell";
import { LicenseQuickFilter } from "./LicenseQuickFilter";
import { FirstLetterUpperCase } from "../helpers/helpersSearchs";
import TuneIcon from "@mui/icons-material/Tune";
import { LicenseStylesColums } from "./licenseStyles/LicenseColumns";

type LicencesTableParams = {
  apiRef: ReturnType<typeof useGridApiRef>;
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
};

const SetActions = (dispatch: ThunkDispatch<any, any, any>) => {
  return {
    setLicenseId: (id: string) => dispatch(setLicenseId(id)),
    focusedLicense: (id: string) => dispatch(focusedLicense(id)),
    setOpenCells: (open: boolean) => dispatch(setOpenCells(open)),
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(false));
      dispatch(setPreselectedFilterField(field));
      setTimeout(() => {
        dispatch(setShowFilterForm(true));
      }, 50);
    },
  };
};

export const LicencesTable: FC<LicencesTableParams> = ({
  apiRef,
  initData,
  t,
}) => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const loading = useSelector(selectLicenseLoading);
  const modalLicense = useSelector(selectModalLicense);
  const { licenseId } = useSelector(licenseAssignation);
  const notification = useSelector(selectLicenseNotification);
  const { licenseColumns, licensesRows } = useLicenseColumns(t, dispatch);
  const stableRows = useMemo(() => licensesRows, [licensesRows]);
  const { setLicenseId, focusedLicense, onOpenFilter } = SetActions(dispatch);
  const [loadingAssignLicense, setLoadingAssignLicense] = useState(false);

  const { filterModel } = useSelector(LicensesFilters);
  const unassignedCount = useSelector(selectUnassignedCount) as number;
  const expiredCount = useSelector(selectExpiredCount) as number;
  const upcomingCount = useSelector(selectUpcomingCount) as number;
  const totalCount = useSelector(selectTotalLicensesCount) as number;
  const [activeFilter, setActiveFilter] = useState<string>("all");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState<boolean>(false);

  const activeCount = Math.max(totalCount - unassignedCount - expiredCount, 0);

  const filterOptions = [
    {
      key: "all",
      label: FirstLetterUpperCase(t("licenses.all")),
      count: totalCount,
      color: LicenseStylesColums.statusColors.all.color,
      bg: LicenseStylesColums.statusColors.all.bg,
    },
    {
      key: "active",
      label: FirstLetterUpperCase(t("licenses.active")),
      count: activeCount > 0 ? activeCount : 0,
      color: LicenseStylesColums.statusColors.active.color,
      bg: LicenseStylesColums.statusColors.active.bg,
    },
    {
      key: "unassigned",
      label: t("licenses.unassigned"),
      count: unassignedCount,
      color: LicenseStylesColums.statusColors.unassigned.color,
      bg: LicenseStylesColums.statusColors.unassigned.bg,
    },
    {
      key: "upcoming",
      label: FirstLetterUpperCase(t("licenses.upcoming")),
      count: upcomingCount,
      color: LicenseStylesColums.statusColors.upcoming.color,
      bg: LicenseStylesColums.statusColors.upcoming.bg,
    },
    {
      key: "expired",
      label: FirstLetterUpperCase(t("licenses.expired")),
      count: expiredCount,
      color: LicenseStylesColums.statusColors.expired.color,
      bg: LicenseStylesColums.statusColors.expired.bg,
    },
  ];

  const tableOptions = [
    {
      option: "openCells",
      label: allExpanded ? t("licenses.closeCells") : t("licenses.openCells"),
      icon: TuneIcon,
      action: () => {
        const allGroupIds = [
          ...new Set(
            apiRef.current
              .getAllRowIds()
              .map((id) => apiRef.current.getRowNode(id)?.parent)
              .filter((parent): parent is string => typeof parent === "string"),
          ),
        ];

        if (allExpanded) {
          allGroupIds.forEach((id) =>
            apiRef.current.setRowChildrenExpansion(id, false),
          );
          setExpandedRows(new Set());
        } else {
          allGroupIds.forEach((id) =>
            apiRef.current.setRowChildrenExpansion(id, true),
          );

          setExpandedRows(new Set(allGroupIds));
        }
        setAllExpanded(!allExpanded);
      },
    },
  ];

  const optionsQuickFilter = () => (
    <LicenseQuickFilter
      filterOptions={filterOptions}
      activeFilter={activeFilter}
      onFilterChange={setActiveFilter}
      filterModel={filterModel}
      setFilterModels={(model) => dispatch(setFilterModel(model))}
      t={t}
    />
  );

  return (
    <Box>
      <ThemeProvider theme={themeInarix(locale)}>
        <div className="container-licenses-table">
          <Box
            sx={{
              height: "calc(97vh - 220px)",
              minHeight: 400,
              typography: "body1",
            }}
          >
            {optionsQuickFilter()}
            <DataGridPro
              apiRef={apiRef}
              rows={stableRows}
              columns={licenseColumns}
              rowHeight={70}
              pagination
              disableRowSelectionOnClick={true}
              disableColumnSelector={true}
              treeData
              getTreeDataPath={(row) => row.path}
              defaultGroupingExpansionDepth={0}
              density="compact"
              isGroupExpandedByDefault={(node) =>
                expandedRows.has(String(node.id))
              }
              groupingColDef={{
                headerName: t("Data.table.Username"),
                width: 260,
                renderHeader(params) {
                  return (
                    <CustomGeneralColumnHeader
                      headerName={t("Data.table.Username")}
                      field={"username"}
                      filterModel={filterModel}
                      availableFilters={["$ilike", "$eq", "$ne"]}
                      onOpenFilter={() => onOpenFilter("username")}
                      backgroundColor="#fafafa"
                      newOptions={tableOptions}
                    />
                  );
                },
                renderCell: (params) => (
                  <GroupCell
                    params={params}
                    t={t}
                    onAssign={(id) => setLicenseId(id)}
                    onToggle={(id, expanded) => {
                      setExpandedRows((prev) => {
                        const next = new Set(prev);
                        expanded ? next.add(id) : next.delete(id);
                        return next;
                      });
                    }}
                  />
                ),
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              ignoreDiacritics={true}
              localeText={
                {
                  ...getDataGridLocale(locale),
                  noRowsLabel: managingFiltersText(
                    filterModel,
                    t,
                    licensesRows,
                  ),
                } as any
              }
            />
          </Box>
        </div>
        <Modal open={!!modalLicense} onClose={() => focusedLicense("")}>
          <LicensePacksModal />
        </Modal>

        <Modal
          open={!!licenseId}
          onClose={loadingAssignLicense ? undefined : () => setLicenseId("")}
        >
          <LicenseAssignationModal
            initData={initData}
            t={t}
            setLoadingAssignLicense={setLoadingAssignLicense}
            loadingAssignLicense={loadingAssignLicense}
          />
        </Modal>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => dispatch(clearLicenseNotification())}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => dispatch(clearLicenseNotification())}
            severity={notification.severity}
          >
            {notification.message ? t(`licenses.${notification.message}`) : ""}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </Box>
  );
};
